import {
    Dialog,
    DialogContent,
    IconButton,
    Toolbar,
    Typography,
} from '@mui/material';
import { DefaultOpenModalProps } from 'components/button/OpenModalButton/OpenModalButton';
import { Shipment } from 'types';
import { Close } from '@mui/icons-material';
import styles from './ShipmentModal.module.scss';
import { getDateWithTime, getMonthDayYear } from 'helpers/dates';
import {
    DataGrid,
    GridColDef,
    GridToolbar,
    GridCsvExportOptions,
} from '@mui/x-data-grid';
import DeviceStatus from 'components/Devices/DeviceStatus/DeviceStatus';
import { getNextSteps } from 'store/services/device-status';
import { toTitleCase } from 'helpers';
import { useNavigate } from 'react-router-dom';
import {
    constructShortDeviceIdQueryString,
    useLazyGetSupplierDevicesEsQuery,
} from 'store/services';
import { useAuth } from 'hooks';
import { useEffect } from 'react';
import { DataGridPlaceholder } from 'components/DataGridPlaceholder/DataGridPlaceholder';
import { TrackingLink } from 'views/Monitors/components/TrackingLink/TrackingLink';

export interface ShipmentModalProps extends DefaultOpenModalProps {
    /** The shipment to show details for. */
    shipment: Shipment | undefined;
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
}

/** Modal that shows the details of a particular shipment. */
export default function ShipmentModal({
    modalOpen,
    setModalOpen,
    shipment,
    viewAsSupplier,
}: ShipmentModalProps) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [query, { data, isLoading, isFetching }] =
        useLazyGetSupplierDevicesEsQuery();

    useEffect(() => {
        if (shipment) {
            const shortIdQueryString = constructShortDeviceIdQueryString([
                shipment,
            ]);
            query({
                userUid: user?.uid ?? '',
                supplierId: viewAsSupplier ?? user?.protected?.supplierId ?? '',
                queryConfig: {
                    limit: 1000,
                    shortIdQueryString,
                    sortField: 'shortDeviceId',
                    isAdminUser:
                        user?.protected?.supplierId?.toString() === '0',
                },
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, viewAsSupplier, shipment]);

    const columns: GridColDef[] = [
        {
            field: 'shortDeviceId',
            headerName: 'Device ID',
            flex: 0.5,
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'status',
            headerName: 'Status',
            renderCell: (params) => {
                const status = params.row.status;
                return <DeviceStatus status={status} />;
            },
            valueFormatter: (params) => {
                return toTitleCase(params.value);
            },
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'nextSteps',
            headerName: 'Next Steps',
            valueGetter: (params) => getNextSteps(params.row.status),
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'address',
            headerName: 'Tank Address',
            flex: 2,
            sortingOrder: ['asc', 'desc'],
        },
    ];

    const csvOptions: GridCsvExportOptions = {
        fileName: `tank-utility-shipment-${
            shipment?.time ? getMonthDayYear(shipment.time) : ''
        }-${shipment?.type_of_order ?? ''}`,
    };

    return (
        <Dialog
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            data-testid="shipment-modal"
            fullWidth
            maxWidth="md"
        >
            <Toolbar>
                <Typography className={styles.modalHeader} variant="h6">
                    Shipment Details
                </Typography>
                <IconButton
                    color="inherit"
                    onClick={() => setModalOpen(false)}
                    aria-label="close"
                >
                    <Close />
                </IconButton>
            </Toolbar>

            <DialogContent>
                <div className={styles.shipmentMetaData}>
                    <div className={styles.metaData}>
                        <Typography
                            data-testid="shipment-quantity"
                            color="text.secondary"
                            variant="subtitle1"
                            className={styles.fieldName}
                        >
                            Quantity:
                        </Typography>
                        <div>{shipment?.qty}</div>
                    </div>

                    <div className={styles.metaData}>
                        <Typography
                            data-testid="shipment-date"
                            color="text.secondary"
                            variant="subtitle1"
                            className={styles.fieldName}
                        >
                            Order Date:
                        </Typography>
                        <div>
                            {shipment?.time
                                ? getDateWithTime(shipment.time)
                                : ''}
                        </div>
                    </div>

                    <div className={styles.metaData}>
                        <Typography
                            data-testid="order-type"
                            color="text.secondary"
                            variant="subtitle1"
                            className={styles.fieldName}
                        >
                            Order Type:
                        </Typography>
                        <div>{shipment?.type_of_order}</div>
                    </div>

                    <div className={styles.metaData}>
                        <Typography
                            data-testid="tracking-link"
                            color="text.secondary"
                            variant="subtitle1"
                            className={styles.fieldName}
                        >
                            Tracking:
                        </Typography>
                        <div>
                            <TrackingLink
                                trackingLink={shipment?.trackingLink}
                                deliveryCarrier={shipment?.delivery_carrier}
                            />
                        </div>
                    </div>
                </div>

                {!isLoading && !isFetching ? (
                    <DataGrid
                        autoHeight
                        sx={{
                            '& .MuiDataGrid-row:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        columns={columns}
                        rows={data?.results ?? []}
                        getRowId={(device) => device.deviceId}
                        components={{ Toolbar: GridToolbar }}
                        componentsProps={{ toolbar: { csvOptions } }}
                        onRowClick={(params) =>
                            navigate(`/device-details/${params.row.deviceId}`, {
                                state: { device: params?.row },
                            })
                        }
                        initialState={{
                            sorting: {
                                sortModel: [
                                    { field: 'shortDeviceId', sort: 'asc' },
                                ], // not sure about default sort on this one
                            },
                        }}
                        disableDensitySelector
                        disableColumnMenu
                        disableColumnFilter
                    />
                ) : (
                    <DataGridPlaceholder />
                )}
            </DialogContent>
        </Dialog>
    );
}
